import React from "react";
import { mdiCodeJson } from "@mdi/js";
import { cn } from "../../../styles";
import { Button, ButtonVariant, Typography } from "../../../atoms";
import { ThemeColor } from "../../../props";
import { LocalizationScope } from "../../../util/LocalizationScope";
import { JSONEditor } from "../../../organisms/JSONEditor";
import { BaseFormFieldProps, FormFieldConstraint } from "../index";
import { processErrorContent } from "../helper";

function tryFormat(value: string): string | null {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return null;
  }
}

export interface FormFieldJSONEditorProps extends BaseFormFieldProps {
  constraints: FormFieldConstraint;
  value: string | null;
  onChangeField: (id: string, value: string | null) => void;
  height?: string;
}

export const JSONEditorField: React.FC<FormFieldJSONEditorProps> = ({
  className,
  id,
  value,
  onChangeField,
  label = "Label",
  localKey,
  helper = null,
  helperKey,
  errors = [],
  touched = false,
  disabled = false,
  readOnly,
  keepHeight,
  noLabel,
  dense,
  constraints,
  height,
}) => {
  const error = touched && errors[0];

  const handleFormat = () => {
    const formatted = tryFormat(value ?? "");
    if (formatted !== null) onChangeField(id, formatted);
  };

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex items-center justify-between mb-1">
          <div className="flex">
            <Typography.Label
              htmlFor={id}
              className={cn(
                "block font-medium select-none",
                dense ? "text-xs" : "text-sm",
              )}
              content={label || { local: id }}
            />
            {!!constraints.presence && (
              <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
            )}
          </div>
          <Button
            variant={ButtonVariant.text}
            color={ThemeColor.gray}
            iconPath={mdiCodeJson}
            dense
            muted
            disabled={disabled || readOnly}
            onClick={handleFormat}
          />
        </div>
      )}
      <div
        className={cn(
          "rounded-md border overflow-hidden",
          error
            ? "border-error-500 dark:border-error-400"
            : "border-back-300 dark:border-back-600",
          disabled && "opacity-50 pointer-events-none",
        )}
      >
        <JSONEditor
          name={id}
          value={value ?? ""}
          onChange={(v) => onChangeField(id, v || null)}
          disabled={disabled}
          readOnly={readOnly}
          height={height}
          noHeader
        />
      </div>
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
    </div>
  );
};

JSONEditorField.displayName = "FormField.JSONEditor";

// Named after the component (Storybook title "FormField/JSONEditor", FormField.JSONEditor) so
// tooling that resolves a component by its title, like the AI docs build, can find it.
export { JSONEditorField as JSONEditor };
